import * as amplitude from '@amplitude/analytics-browser';
import { appConfig } from '@/config/app.config';
import { ISubscriber, IUser } from '@/types/resource.types';

class AmplitudeService {
    private initialized = false;

    init(): void {
        if (!appConfig.AMPLITUDE_API_KEY) return;
        amplitude.init(appConfig.AMPLITUDE_API_KEY, {
            defaultTracking: false
        });
        this.initialized = true;
    }

    async identifyUser(user: IUser, subscriber: ISubscriber | null, oktaId?: string): Promise<void> {
        if (!this.initialized) return;

        const userId = oktaId?.trim() || `verify-user-${subscriber?.id ?? 'local'}-${user.id}`;

        amplitude.setUserId(userId);

        const identify = new amplitude.Identify();
        identify.set('email', user.email);
        amplitude.identify(identify);

        if (subscriber) {
            amplitude.setGroup('tenant', subscriber.id.toString());
        }
    }

    trackUserLogin(props: {
        userName: string;
        loginAttemptResult: string;
        accountName?: string;
        accountSegment?: string;
    }): void {
        if (!this.initialized) return;
        amplitude.track('UserLogin', {
            userName: props.userName,
            loginAttemptResult: props.loginAttemptResult,
            accountName: props.accountName ?? '',
            accountSegment: props.accountSegment ?? ''
        });
    }

    trackProjectOpened(props: {
        projectId: string | number;
        numberFilesInProject: number;
        userTenancyAccount?: string | number;
        openedByUser?: string;
    }): void {
        if (!this.initialized) return;
        amplitude.track('ProjectOpened', {
            numberFilesInProject: props.numberFilesInProject,
            userTenancyAccount: props.userTenancyAccount?.toString() ?? '',
            openedByUser: props.openedByUser ?? ''
        }, {
            groups: { case: props.projectId.toString() }
        });
    }

    trackFileOpened(props: {
        fileId: string | number;
        projectId: string | number;
        userTenancyAccount?: string | number;
        openedByUser?: string;
    }): void {
        if (!this.initialized) return;
        amplitude.track('FileOpened', {
            userTenancyAccount: props.userTenancyAccount?.toString() ?? '',
            openedByUser: props.openedByUser ?? ''
        }, {
            groups: { case: props.projectId.toString() }
        });
    }

    trackButtonClick(
        buttonNameUri: string,
        projectId?: string | number,
        eventProperties: Record<string, string> = {}
    ): void {
        if (!this.initialized) return;
        const groups: Record<string, string> = {};
        if (projectId) groups['case'] = projectId.toString();
        amplitude.track('ButtonClick', { ...eventProperties, buttonNameUri }, { groups });
    }

    trackNotificationRead(notificationTitle: string): void {
        if (!this.initialized) return;
        amplitude.track('NotificationRead', { notificationTitle });
    }
}

export default new AmplitudeService();
